import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import {
  HiOutlineBell,
  HiOutlineExclamationTriangle,
  HiOutlineClock,
  HiOutlineCalendarDays,
  HiOutlineArrowPath,
  HiOutlineUserPlus,
  HiOutlineChatBubbleLeftEllipsis,
} from 'react-icons/hi2'
import { useNotificationFeed } from './useNotificationFeed'
import { useNotificationsRead } from '@/store/notificationsRead'
import type { NotificationType } from '@/types'
import { fromNow } from '@/utils/format'
import { cn } from '@/utils/cn'

const iconMap: Record<NotificationType, JSX.Element> = {
  reminder: <HiOutlineClock />,
  overdue: <HiOutlineClock />,
  meeting: <HiOutlineCalendarDays />,
  followup: <HiOutlineArrowPath />,
  assignment: <HiOutlineUserPlus />,
  status: <HiOutlineArrowPath />,
  escalation: <HiOutlineExclamationTriangle />,
  mention: <HiOutlineChatBubbleLeftEllipsis />,
}

const toneMap: Record<NotificationType, string> = {
  reminder: 'bg-primary-50 text-primary-700',
  overdue: 'bg-warning-50 text-warning-700',
  meeting: 'bg-secondary-50 text-secondary-700',
  followup: 'bg-accent-50 text-accent-700',
  assignment: 'bg-primary-50 text-primary-700',
  status: 'bg-slate-100 text-slate-600',
  escalation: 'bg-danger-50 text-danger-700',
  mention: 'bg-purple-50 text-purple-700',
}

export function NotificationCenter() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Close the panel when clicking anywhere outside it (bell + dropdown).
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Feed is derived live from tasks + meetings; read state lives in its own store.
  const items = useNotificationFeed()
  const unread = items.filter((n) => !n.read).length
  const markRead = useNotificationsRead((s) => s.markRead)
  const markAllRead = useNotificationsRead((s) => s.markAllRead)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Notifications"
      >
        <HiOutlineBell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              className="absolute right-0 z-40 mt-2 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-popover shadow-elevated"
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <p className="text-sm font-semibold">
                  Notifications {unread > 0 && <span className="text-muted-foreground">· {unread} new</span>}
                </p>
                <button
                  onClick={() => markAllRead(items.map((n) => n.id))}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Mark all read
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {items.length === 0 ? (
                  <p className="px-4 py-10 text-center text-sm text-muted-foreground">You're all caught up.</p>
                ) : (
                  items.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => {
                        markRead(n.id)
                        if (n.link) navigate(n.link)
                        setOpen(false)
                      }}
                      className={cn(
                        'flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-muted/60',
                        !n.read && 'bg-primary-50/40 dark:bg-primary-500/15',
                      )}
                    >
                      <span className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-base', toneMap[n.type])}>
                        {iconMap[n.type]}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-foreground">{n.title}</span>
                          {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{n.body}</span>
                        <span className="mt-1 block text-[11px] text-slate-400">{fromNow(n.at)}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
